/**
 * Personal/account-scoped Installed Tools contracts. Derived from the org
 * contract via `.omit`/`.exclude` so shared columns can't drift; drops the
 * `userCount`/`users` column and is scoped to the requesting user (org-agnostic).
 * Re-adds `sources`/`extras` (org-DataTable drops them; the personal expanded
 * view renders them) with shapes inlined to mirror the legacy tool schema.
 */
import { z } from 'zod';
import { toolApprovalQueryParamSchema, } from './shadow-ai-tool-approvals';
import { baseListQuerySchema, sortOrderSchema, } from './helpers';
import { createTableQuerySchema, FilterOperators, FiltersSchema, FilterValueSchema, TableResponseMetaSchema, } from './table';
import { Severity, severitySchema } from './shared/severity';
import { installedToolListItemSchema, installedToolsSortFieldSchema, installedToolsFilterOptionFieldSchema, INSTALLED_TOOLS_FILTERABLE_FIELDS, INSTALLED_TOOLS_SORTABLE_FIELDS, INSTALLED_TOOLS_FILTER_OPTION_FIELDS, INSTALLED_TOOLS_FILTER_COLUMN_DATA_TYPES, INSTALLED_TOOLS_OPERATOR_OVERRIDES, } from './shadow-ai-installed-tools';
export { INSTALLED_TOOLS_FILTERABLE_FIELDS };
// ============================================================================
// Sortable Fields (personal — `users` dropped)
// ============================================================================
export const personalInstalledToolsSortFieldSchema = installedToolsSortFieldSchema.exclude([
    INSTALLED_TOOLS_SORTABLE_FIELDS.USERS,
    // KIR-3429: logged-in-user is org-only; the personal screen has no column.
    INSTALLED_TOOLS_SORTABLE_FIELDS.LOGGED_IN_USER,
    // KIR-5098: endpoint is org-only too.
    INSTALLED_TOOLS_SORTABLE_FIELDS.ENDPOINT,
]);
// ============================================================================
// Filter Option Fields (dynamic endpoints only — `users` dropped)
// ============================================================================
export const personalInstalledToolsFilterOptionFieldSchema = installedToolsFilterOptionFieldSchema.exclude([
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.USERS,
    // KIR-3429: logged-in-user is org-only; the personal screen has no column.
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.LOGGED_IN_USER,
    // KIR-5098: endpoint is org-only too.
    INSTALLED_TOOLS_FILTER_OPTION_FIELDS.ENDPOINT,
]);
// ============================================================================
// Response Item (org row minus `userCount`, plus expanded-view `sources`/`extras`)
// ============================================================================
// Only the `sources`/`extras` shapes are inlined; the row still derives from the
// org contract via `.omit` above. Shapes mirror shadowAiToolListItemSchema.
export const personalInstalledToolListItemSchema = installedToolListItemSchema
    .omit({ userCount: true })
    .extend({
    sources: z.array(z.string()),
    extras: z.record(z.string(), z.string()).nullable(),
});
// ============================================================================
// Filter Column Data Types (private — used internally by the query schema)
// ============================================================================
// Org column map minus the dropped `users` column (derived, not re-authored).
const { [INSTALLED_TOOLS_FILTERABLE_FIELDS.USERS]: _users, [INSTALLED_TOOLS_FILTERABLE_FIELDS.ENDPOINT]: _endpoint, ...PERSONAL_INSTALLED_TOOLS_FILTER_COLUMN_DATA_TYPES } = INSTALLED_TOOLS_FILTER_COLUMN_DATA_TYPES;
// Name: includes/excludes/contains/not-contains plus value-less — no starts-with/ends-with.
export const PERSONAL_INSTALLED_TOOLS_OPERATOR_OVERRIDES = {
    ...INSTALLED_TOOLS_OPERATOR_OVERRIDES,
    [INSTALLED_TOOLS_FILTERABLE_FIELDS.NAME]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.CONTAINS,
        FilterOperators.NOT_CONTAINS,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
// ============================================================================
// Filter Options Schemas
// ============================================================================
export const personalInstalledToolsFilterOptionsQuerySchema = z.object({
    field: personalInstalledToolsFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
export const personalInstalledToolsFilterOptionsResponseSchema = z.object({
    field: personalInstalledToolsFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const personalInstalledToolsTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: personalInstalledToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    // KIR-4864: Show filter (not_approved | approved | all) against the
    // user's personal approvals. Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
export const personalInstalledToolsTableQuerySchema = createTableQuerySchema(personalInstalledToolsTableQueryBaseSchema, PERSONAL_INSTALLED_TOOLS_FILTER_COLUMN_DATA_TYPES, PERSONAL_INSTALLED_TOOLS_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        if (filter.field === INSTALLED_TOOLS_FILTERABLE_FIELDS.RISK) {
            const validRisk = severitySchema.exclude([Severity.Critical]);
            filter.value.forEach((v, valueIndex) => {
                if (!validRisk.safeParse(v).success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for risk field must be one of: ${validRisk.options.join(', ')}`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
        if (filter.field === INSTALLED_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED ||
            filter.field === INSTALLED_TOOLS_FILTERABLE_FIELDS.LAST_SEEN) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for ${filter.field} field must be valid ISO 8601 datetime strings`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
});
// ============================================================================
// API Client Params Schema (for serializing DataTable query params)
// ============================================================================
export const listPersonalInstalledToolsDataTableParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: personalInstalledToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: z.array(FilterValueSchema).optional(),
    // KIR-4864: Show filter. Omitted = all.
    approval: toolApprovalQueryParamSchema,
});
// ============================================================================
// Table Response Schema (org wrapper shape; no lastHeartbeat)
// ============================================================================
export const personalInstalledToolsTableResponseSchema = z.object({
    data: z.array(personalInstalledToolListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
//# sourceMappingURL=account-shadow-ai-installed-tools.js.map